import React from 'react';
import { Paper, ActionIcon, Tooltip, Divider, Flex } from '@mantine/core';
import { MapPinOff, Undo, Redo, CircleOff, Trash } from 'lucide-react';

interface ContextMenuProps {
    contextMenu: { x: number; y: number };
    activeIndex: number | null;
    history: string[];
    redoStack: string[];
    currentPoints: string;
    resetActiveIndex: () => void;
    closeContextMenu: () => void;
    onPointsChange: (updatedPoints: string) => void;
    setHistory: React.Dispatch<React.SetStateAction<string[]>>;
    setRedoStack: React.Dispatch<React.SetStateAction<string[]>>;
    setCurrentPoints: React.Dispatch<React.SetStateAction<string>>;
    t: (key: string) => string;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
    contextMenu,
    activeIndex,
    history,
    redoStack,
    currentPoints,
    resetActiveIndex,
    closeContextMenu,
    onPointsChange,
    setHistory,
    setRedoStack,
    setCurrentPoints,
    t
}) => {
    const undo = () => {
        if (history.length === 0) return;
        const lastPoints = history[history.length - 1];
        setHistory(prev => prev.slice(0, -1));
        setRedoStack(prev => [currentPoints, ...prev]);
        setCurrentPoints(lastPoints);
        onPointsChange(lastPoints);
    };

    const redo = () => {
        if (redoStack.length === 0) return;
        const nextPoints = redoStack[0];
        setRedoStack(prev => prev.slice(1));
        setHistory(prev => [...prev, currentPoints]);
        setCurrentPoints(nextPoints);
        onPointsChange(nextPoints);
    };

    const clearPoints = () => {
        resetActiveIndex();
        setHistory(prev => [...prev, currentPoints]);
        setCurrentPoints('');
        onPointsChange('');
    };

    const removeLastPoint = () => {
        const pointsArray = currentPoints.split(' ').map(point => point.split(','));
        if (pointsArray.length > 0) {
            pointsArray.pop();
            const updatedPoints = pointsArray.map(point => point.join(',')).join(' ');
            setHistory(prev => [...prev, currentPoints]);
            setCurrentPoints(updatedPoints);
            onPointsChange(updatedPoints);
        }
    };

    return (
        <Paper
            style={{
                position: 'absolute',
                top: contextMenu.y,
                left: contextMenu.x,
                borderRadius: '10px',
                border: '2px solid gray',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                zIndex: 1000,
            }}
            onMouseLeave={closeContextMenu}
        >
            {activeIndex !== null && (
                <>
                    <Tooltip label="Deselect Point">
                        <ActionIcon onClick={resetActiveIndex} disabled={history.length === 0}>
                            <MapPinOff size={18} />
                        </ActionIcon>
                    </Tooltip>
                    
                    <Divider />
                </>
            )}

            <Flex gap={10}>
                <Tooltip label={t('scheme.redactor.buttons.undo')}>
                    <ActionIcon onClick={undo} disabled={history.length === 0}>
                        <Undo size={18} />
                    </ActionIcon>
                </Tooltip>

                <Tooltip label={t('scheme.redactor.buttons.redo')}>
                    <ActionIcon onClick={redo} disabled={redoStack.length === 0}>
                        <Redo size={18} />
                    </ActionIcon>
                </Tooltip>
            </Flex>

            <Flex gap={10}>
                <Tooltip label={t('scheme.redactor.panelOptions.setFigure.polilyne.polilyneEditor.contextMenu.removeLastPoint')}>
                    <ActionIcon
                        onClick={removeLastPoint}
                        color="red"
                    >
                        <CircleOff size={18} />
                    </ActionIcon>
                </Tooltip>

                <Tooltip label={t('scheme.redactor.panelOptions.setFigure.polilyne.polilyneEditor.contextMenu.clearPoints')}>
                    <ActionIcon
                        onClick={clearPoints}
                        color="red"
                    >
                        <Trash size={18} />
                    </ActionIcon>
                </Tooltip>
            </Flex>
        </Paper>
    );
};

export default ContextMenu;
